"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import Alert from "../information/alert";
import { AlertCircle } from "lucide-react";

export interface FormError {
  /** Field name/id the error relates to */
  field: string;
  /** Error message */
  message: string;
}

export interface FormErrorSummaryProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of form errors to display */
  errors: FormError[];
  /** Title for the error summary */
  title?: string;
  /** Whether to auto-focus the summary when errors appear */
  autoFocus?: boolean;
  /** Callback when a field link is clicked */
  onFieldClick?: (field: string) => void;
}

/**
 * FormErrorSummary displays a list of form validation errors at the top of a form.
 * Use it for form-level validation to help users quickly identify all issues.
 */
export function FormErrorSummary({
  className,
  errors,
  title = "Please fix the following errors:",
  autoFocus = true,
  onFieldClick,
  ...props
}: FormErrorSummaryProps) {
  const summaryRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (autoFocus && errors.length > 0 && summaryRef.current) {
      summaryRef.current.focus();
    }
  }, [errors, autoFocus]);

  if (errors.length === 0) {
    return null;
  }

  const handleFieldClick = (field: string) => {
    // Try to focus the field
    const element = document.getElementById(field);
    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    onFieldClick?.(field);
  };

  return (
    <div
      ref={summaryRef}
      tabIndex={-1}
      role="alert"
      aria-labelledby="error-summary-title"
      className={cn("outline-none", className)}
      {...props}
    >
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <div className="ml-2">
          <h4 id="error-summary-title" className="font-medium">
            {title}
          </h4>
          <ul className="mt-2 list-disc list-inside text-sm space-y-1">
            {errors.map((error, index) => (
              <li key={`${error.field}-${index}`}>
                {onFieldClick ? (
                  <button
                    type="button"
                    className="underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive rounded"
                    onClick={() => handleFieldClick(error.field)}
                  >
                    {error.message}
                  </button>
                ) : (
                  <a
                    href={`#${error.field}`}
                    className="underline hover:no-underline"
                    onClick={(e) => {
                      e.preventDefault();
                      handleFieldClick(error.field);
                    }}
                  >
                    {error.message}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Alert>
    </div>
  );
}

FormErrorSummary.displayName = "FormErrorSummary";

/**
 * useFormErrors hook for managing form error state.
 */
export function useFormErrors() {
  const [errors, setErrors] = React.useState<FormError[]>([]);

  const addError = React.useCallback((field: string, message: string) => {
    setErrors((prev) => [...prev.filter((e) => e.field !== field), { field, message }]);
  }, []);

  const removeError = React.useCallback((field: string) => {
    setErrors((prev) => prev.filter((e) => e.field !== field));
  }, []);

  const clearErrors = React.useCallback(() => {
    setErrors([]);
  }, []);

  const hasError = React.useCallback(
    (field: string) => errors.some((e) => e.field === field),
    [errors]
  );

  const getError = React.useCallback(
    (field: string) => errors.find((e) => e.field === field)?.message,
    [errors]
  );

  return {
    errors,
    setErrors,
    addError,
    removeError,
    clearErrors,
    hasError,
    getError,
    hasErrors: errors.length > 0,
  };
}

export default FormErrorSummary;
